import { getScreenName } from 'utils/screenNames';
import { checkTabPresence } from '../hooks/useOpenTabs';
import { clamp, isFiniteNumber, positiveOr } from './math';

export type PopupOptions = {
  top: number;
  left: number;
  width: number;
  height: number;
  // A workspace layout (V2) spot, worked out from the current displays, so the window opens there
  isFromWorkspaceLayout?: boolean;
};

const DEFAULT_POPUP_OPTIONS: PopupOptions = {
  width: 1000,
  height: 900,
  top: 10,
  left: 10,
};

const MIN_POPUP_EDGE = 1;
const MAX_COORDINATE_ABS = 10000;

export const sanitizePopupOptions = ({ config }: { config: PopupOptions }): PopupOptions => {
  const fallbackWidth = positiveOr(window.innerWidth, DEFAULT_POPUP_OPTIONS.width);
  const fallbackHeight = positiveOr(window.innerHeight, DEFAULT_POPUP_OPTIONS.height);

  const maxWidth = Math.max(MIN_POPUP_EDGE, positiveOr(window.screen?.availWidth, fallbackWidth));
  const maxHeight = Math.max(
    MIN_POPUP_EDGE,
    positiveOr(window.screen?.availHeight, fallbackHeight)
  );

  const rawWidth = isFiniteNumber(config.width) ? config.width : DEFAULT_POPUP_OPTIONS.width;
  const rawHeight = isFiniteNumber(config.height) ? config.height : DEFAULT_POPUP_OPTIONS.height;
  const width = clamp(Math.round(rawWidth), MIN_POPUP_EDGE, maxWidth);
  const height = clamp(Math.round(rawHeight), MIN_POPUP_EDGE, maxHeight);

  const top = isFiniteNumber(config.top)
    ? clamp(Math.round(config.top), -MAX_COORDINATE_ABS, MAX_COORDINATE_ABS)
    : DEFAULT_POPUP_OPTIONS.top;
  const left = isFiniteNumber(config.left)
    ? clamp(Math.round(config.left), -MAX_COORDINATE_ABS, MAX_COORDINATE_ABS)
    : DEFAULT_POPUP_OPTIONS.left;

  return {
    width,
    height,
    top,
    left,
  };
};

export const openPopup = async (
  openPathNewTab: string,
  config: PopupOptions = DEFAULT_POPUP_OPTIONS
): Promise<boolean> => {
  const safeConfig = sanitizePopupOptions({ config });

  let id;
  try {
    id = getScreenName(new URL(openPathNewTab, window.location.origin).toString());
  } catch (e: any) {
    // We couldn't find a screen name, in this case we are okay with a undefined id
  }

  // Open with safe, conservative defaults to avoid off-screen windows, or at the layout spot (V2)
  const neutralDefaults = config.isFromWorkspaceLayout
    ? toWorkspaceLayoutBounds(config)
    : DEFAULT_POPUP_OPTIONS;
  const windowHandle = window.open(
    openPathNewTab,
    // We use the screen name as the window name to allow for the same tab to be reused
    id,
    Object.entries({
      // enforce the popup to use a different JS thread to allot more memory
      noopener: true,
      resizable: 'yes',
      scrollbar: 'yes',
      titlebar: 'yes',
      ...neutralDefaults,
    })
      .map(([key, value]: [any, any]) => `${key}=${String(value)}`)
      .join(',')
  );

  // If window opened and is same-origin, apply saved/sanitized geometry
  if (windowHandle) {
    try {
      // Verify same-origin by attempting to access a property
      if (typeof windowHandle.innerWidth !== 'undefined') {
        windowHandle.moveTo(safeConfig.left, safeConfig.top);
        windowHandle.resizeTo(safeConfig.width, safeConfig.height);

        // Force OS compositor invalidation via nudge
        // This ensures Chromium's renderer repaints after window move/resize,
        // especially critical when moving across monitors with different DPIs
        // where the OS draws the window frame but Chromium's cached display list is stale
        window.setTimeout(() => {
          try {
            // Nudge position by 1 pixel to force OS compositor redraw
            windowHandle.moveTo(safeConfig.left + 1, safeConfig.top);
            // Immediately move back to trigger another invalidation
            window.setTimeout(() => {
              windowHandle.moveTo(safeConfig.left, safeConfig.top);
            }, 10);
          } catch (e: any) {
            // Window may have been closed; silently fail
          }
        }, 50);
      }
    } catch (e: any) {
      // Cross-origin or blocked; the window opened at safe defaults, which is acceptable
    }
  }

  const tabId = await checkTabPresence(openPathNewTab);

  return tabId != null;
};

// Layout spots can be on another display, so their size isn't clamped to this window's screen
const toWorkspaceLayoutBounds = ({ width, height, top, left }: PopupOptions): PopupOptions => ({
  width,
  height,
  top,
  left,
});
